import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.funizzle.com';
const DEFAULT_OG = `${SITE_URL}/og-cover.png`;

const APP_STORE_URL =
  'https://itunes.apple.com/WebObjects/MZStore.woa/wa/redirectToContent?id=6751509285&mt=8';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.funizzle.mobile.app';

const mobileAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'MobileApplication',
  name: 'BingePlay',
  operatingSystem: 'iOS, Android',
  applicationCategory: 'GameApplication',
  description:
    'BingePlay is the TikTok for games. A swipeable feed of playable HTML5 game demos. Try real game levels instantly. Download only the ones you love.',
  url: SITE_URL,
  image: DEFAULT_OG,
  installUrl: [APP_STORE_URL, PLAY_STORE_URL],
  publisher: { '@type': 'Organization', name: 'Funizzle, Inc.' },
};

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'BingePlay',
  url: SITE_URL,
  logo: DEFAULT_OG,
  sameAs: [
    'https://www.instagram.com/funizzle/',
    'https://www.linkedin.com/company/funizzle/',
    'https://discord.com/invite/mN8ENbzEMJ',
  ],
};

export default function Seo({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · BingePlay` : 'BingePlay. The TikTok for Games';
  const url = `${SITE_URL}${path}`;
  const desc =
    description ||
    'BingePlay is the TikTok for games. Swipe through playable demos of real mobile games. Play instantly. Download only what you love.';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="BingePlay" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      <meta name="apple-itunes-app" content="app-id=6751509285" />
      <meta name="google-play-app" content="app-id=com.funizzle.mobile.app" />

      <script type="application/ld+json">{JSON.stringify(mobileAppJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
    </Helmet>
  );
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  ogImage: PropTypes.string,
  noindex: PropTypes.bool,
};
